import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { modifier } from 'ember-modifier';
import { PhX } from 'ember-phosphor-icons';
import type { TOC } from '@ember/component/template-only';

interface DrawerHeaderSignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const DrawerHeader: TOC<DrawerHeaderSignature> = <template>
  {{! Bare header text inherits the drawer title style; explicit heading classes still take precedence. }}
  <div
    data-test-drawer-header
    class="flex-none pl-4 pr-12 py-3 sm:pl-6 sm:py-4 border-b tio-border-default tio-text-lg-semibold"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

interface DrawerBodySignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const DrawerBody: TOC<DrawerBodySignature> = <template>
  <div data-test-drawer-body class="flex-1 overflow-y-auto px-4 py-3 sm:px-6 sm:py-4" ...attributes>
    {{yield}}
  </div>
</template>;

interface DrawerFooterSignature {
  Args: Record<string, never>;
  Blocks: { default: [] };
  Element: HTMLDivElement;
}

const DrawerFooter: TOC<DrawerFooterSignature> = <template>
  <div
    data-test-drawer-footer
    class="flex-none px-4 py-3 sm:px-6 sm:py-4 border-t tio-border-default"
    ...attributes
  >
    {{yield}}
  </div>
</template>;

type Placement = 'left' | 'right' | 'top' | 'bottom';
type Size = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const SIZE_CLASSES: Record<Placement, Record<Size, string>> = {
  // Below the `sm` breakpoint (640px) side drawers go full-width so the panel
  // and its close button stay on-screen on phones; the fixed widths are
  // restored at `sm:` and up.
  right: {
    sm: 'w-full sm:w-80',
    md: 'w-full sm:w-96',
    lg: 'w-full sm:w-[32rem]',
    xl: 'w-full sm:w-[42rem]',
    full: 'w-screen',
  },
  left: {
    sm: 'w-full sm:w-80',
    md: 'w-full sm:w-96',
    lg: 'w-full sm:w-[32rem]',
    xl: 'w-full sm:w-[42rem]',
    full: 'w-screen',
  },
  top: {
    sm: 'h-48',
    md: 'h-64',
    lg: 'h-96',
    xl: 'h-[32rem]',
    full: 'h-screen',
  },
  bottom: {
    sm: 'h-48',
    md: 'h-64',
    lg: 'h-96',
    xl: 'h-[32rem]',
    full: 'h-screen',
  },
};

const PLACEMENT_POSITION: Record<Placement, string> = {
  right: 'inset-y-0 right-0',
  left: 'inset-y-0 left-0',
  top: 'inset-x-0 top-0',
  bottom: 'inset-x-0 bottom-0',
};

/**
 * The closed (off-screen) translate and the axis constraint for each placement.
 */
const PLACEMENT_SLIDE_CLOSED: Record<Placement, string> = {
  right: 'translate-x-full',
  left: '-translate-x-full',
  top: '-translate-y-full',
  bottom: 'translate-y-full',
};

const PLACEMENT_SLIDE_OPEN: Record<Placement, string> = {
  right: 'translate-x-0',
  left: 'translate-x-0',
  top: 'translate-y-0',
  bottom: 'translate-y-0',
};

/** Full height/width constraint so the drawer fills its axis */
const PLACEMENT_FILL: Record<Placement, string> = {
  right: 'h-full',
  left: 'h-full',
  top: 'w-full',
  bottom: 'w-full',
};

/** Non-modal panels position from the `--tio-drawer-inset-*` custom properties instead. */
const NON_MODAL_POSITION: Record<Placement, string> = {
  right: 'tio-drawer__panel--non-modal-right',
  left: 'tio-drawer__panel--non-modal-left',
  top: 'tio-drawer__panel--non-modal-top',
  bottom: 'tio-drawer__panel--non-modal-bottom',
};

/** Slide-out panel with Escape/outside dismissal and named header, body, and footer blocks. */
interface DrawerSignature {
  Args: {
    /** Controls panel visibility; closing keeps content mounted for the 300 ms exit transition. */
    isOpen: boolean;
    /** Called when the drawer requests to close */
    onClose: () => void;
    /** Slide-in direction @default "right" */
    placement?: Placement;
    /** Width/height of the drawer @default "md" */
    size?: Size;
    /** Close when clicking the backdrop @default true */
    closeOnOutsideClick?: boolean;
    /** Close when pressing Escape @default true */
    closeOnEscapeKey?: boolean;
    /** Show the default close button in the top-right corner @default true */
    allowCloseButton?: boolean;
    /** Accessible name for that close button @default "Close drawer" */
    closeLabel?: string;
    /** Show the semi-transparent backdrop behind the drawer @default true */
    showBackdrop?: boolean;
    /**
     * Accessible label for the dialog. Must be an arg, not an aria-label
     * attribute: attributes splat onto the panel, and the name belongs on
     * the overlay that carries role="dialog".
     */
    ariaLabel?: string;
    /** Manage browser history (pushState on open, back() on close) @default true
     *  Set to false when the drawer modifies query params on close to avoid
     *  history.back() racing with Ember's URL update. */
    manageHistory?: boolean;
    /**
     * Leaves the page usable while the drawer is open: no backdrop, no scroll
     * lock, no `aria-modal`, and clicks outside the panel reach the page. The
     * panel docks inside `--tio-drawer-inset-top/-right/-bottom/-left`, which
     * default to the viewport edges, so a page can keep its own sticky chrome
     * uncovered, and its shadow reads `--tio-drawer-shadow` so a page that docks it
     * can drop the float. Escape closes it from anywhere on the page unless a
     * popover, modal or non-modal dialog is open on top. Closing returns focus to
     * the element that opened it.
     * @default false
     */
    nonModal?: boolean;
    /**
     * Element to render into instead of `document.body`. Hosting a non-modal
     * drawer inside the page lets the page's inset properties cascade to the
     * panel.
     */
    destination?: HTMLElement;
  };
  Blocks: {
    /** Default block — free-form content (use DrawerHeader/Body/Footer directly) */
    default?: [];
    /** Named block for header content */
    header?: [];
    /** Named block for body content */
    body?: [];
    /** Named block for footer content */
    footer?: [];
  };
  Element: HTMLDivElement;
}

export default class Drawer extends Component<DrawerSignature> {
  /**
   * Internal visibility state that drives the CSS transition.
   * We keep the DOM mounted briefly after @isOpen becomes false
   * so the slide-out animation can play before we remove the element.
   */
  @tracked private _mounted = false;
  @tracked private _visible = false;

  /** Tracks whether the drawer was closed by the browser back button */
  private _closedViaPopstate = false;

  /** Unique ID for this drawer instance — used to scope querySelector */
  private drawerId = crypto.randomUUID();

  /** Portal target — renders drawer at document.body to escape stacking contexts.
   *  In test mode, portal into #ember-testing so test helpers can find
   *  portaled elements via the normal find/click/assert.dom APIs. */
  get portalDestination() {
    return this.args.destination ?? document.getElementById('ember-testing') ?? document.body;
  }

  private get placement() {
    return this.args.placement ?? 'right';
  }

  private get size() {
    return this.args.size ?? 'md';
  }

  private get closeOnOutsideClick() {
    return this.args.closeOnOutsideClick ?? true;
  }

  private get closeOnEscapeKey() {
    return this.args.closeOnEscapeKey ?? true;
  }

  private get showCloseButton() {
    return this.args.allowCloseButton ?? true;
  }

  private get manageHistory() {
    return this.args.manageHistory ?? true;
  }

  private get backdropClass() {
    return this.args.showBackdrop === false ? '' : 'tio-drawer__backdrop--dimmed';
  }

  private get panelPositionClass() {
    return this.args.nonModal
      ? `pointer-events-auto ${NON_MODAL_POSITION[this.placement]}`
      : PLACEMENT_POSITION[this.placement];
  }

  private get panelSizeClass() {
    return SIZE_CLASSES[this.placement]?.[this.size] ?? SIZE_CLASSES.right.md;
  }

  private get panelFillClass() {
    return this.args.nonModal ? '' : PLACEMENT_FILL[this.placement];
  }

  private get panelShadowClass() {
    return this.args.nonModal ? 'tio-drawer__panel--non-modal' : 'shadow-2xl';
  }

  private get slideClass() {
    return this._visible
      ? PLACEMENT_SLIDE_OPEN[this.placement]
      : PLACEMENT_SLIDE_CLOSED[this.placement];
  }

  /**
   * Manages mount/unmount lifecycle to allow CSS transitions.
   * When `isOpen` becomes true, we mount immediately then
   * defer the visible flag to the next frame so the browser
   * can paint the initial (off-screen) state first.
   *
   * When `isOpen` becomes false, we set visible=false to trigger
   * the slide-out, then unmount after the transition duration.
   */
  manageFocus = modifier((_element: HTMLElement, [isOpen]: [boolean]) => {
    let rafId: number | undefined;
    // Capture scroll position so the teardown can restore it if the modifier
    // is torn down before the close path runs (e.g. route transition).
    let savedScrollY = 0;
    const nonModal = this.args.nonModal === true;
    const opener = document.activeElement;
    // Focus can leave a non-modal drawer for the page, so Escape is heard document-wide; keydowns
    // from inside the panel stop at handleKeyDown. Anything layered above it — an open popover,
    // a modal, or a non-modal dialog — owns the press, so one Escape never closes two layers.
    const onDocumentKeydown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || !this.closeOnEscapeKey) return;
      if (
        document.querySelector('.reimagined-popover, [aria-modal="true"], .tio-modal--non-modal')
      ) {
        return;
      }
      this.args.onClose();
    };

    // Browser back-button integration — hoisted so teardown can access them.
    let historyDrawerId: string | undefined;
    let didPushHistory = false;
    const onPopstate = (event: PopStateEvent) => {
      // If we're navigating TO our own pushed entry (another drawer above us in
      // history called back() and landed on our state), our drawer is still open.
      // Don't close — only close when navigating AWAY from our entry.
      if (event.state?.drawerId === historyDrawerId) return;
      this._closedViaPopstate = true;
      window.removeEventListener('popstate', onPopstate);
      this.args.onClose();
    };

    if (isOpen) {
      this._mounted = true;

      // Lock body scroll — preserve scroll position using the fixed-body pattern
      // so the page doesn't jump when overflow is hidden.
      if (!nonModal) {
        savedScrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${savedScrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.overflow = 'hidden';
      } else {
        document.addEventListener('keydown', onDocumentKeydown);
      }

      rafId = requestAnimationFrame(() => {
        rafId = undefined;
        this._visible = true;

        // Focus the dialog container for keyboard nav.
        // Scope to the modifier element to avoid selecting a different drawer.
        // preventScroll avoids the browser scrolling to the focused element.
        const dialog = document.querySelector<HTMLElement>(`[data-drawer-id="${this.drawerId}"]`);
        if (dialog) {
          dialog.focus({ preventScroll: true });
        }
      });

      // Push a history entry so the back button closes the drawer instead
      // of navigating away. Capture whether we installed history integration
      // so teardown always cleans up what was set up, even if @manageHistory
      // changes while the drawer is open.
      didPushHistory = false;
      if (this.manageHistory) {
        didPushHistory = true;
        historyDrawerId = this.drawerId;
        this._closedViaPopstate = false;
        history.pushState({ drawerId: historyDrawerId }, '');
        window.addEventListener('popstate', onPopstate);
      }
    } else if (this._mounted) {
      this._visible = false;

      // Wait for the CSS transition to finish before un-mounting
      const cleanup = setTimeout(() => {
        this._mounted = false;
      }, 300); // matches transition duration

      // Scroll restore happens in the teardown below so it only runs once,
      // regardless of whether the close path or a route-transition triggers it.
      return () => clearTimeout(cleanup);
    }

    return () => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
      }

      document.removeEventListener('keydown', onDocumentKeydown);

      if (nonModal && !this.args.isOpen && opener instanceof HTMLElement && opener.isConnected) {
        const dialog = document.querySelector(`[data-drawer-id="${this.drawerId}"]`);
        if (dialog?.contains(document.activeElement)) {
          opener.focus({ preventScroll: true });
        }
      }

      // Clean up browser history integration
      if (didPushHistory) {
        window.removeEventListener('popstate', onPopstate);
        if (
          historyDrawerId &&
          !this._closedViaPopstate &&
          !this.args.isOpen &&
          history.state?.drawerId === historyDrawerId
        ) {
          // Drawer closed programmatically (X, Escape, backdrop) — remove
          // the history entry we pushed so the user doesn't have a phantom
          // back-button press.
          history.back();
        }
        this._closedViaPopstate = false;
      }

      // Restore body scroll — single restore point for both normal close
      // and unexpected teardown (route transition, component destroy).
      if (!nonModal && document.body.style.position === 'fixed') {
        const scrollY = savedScrollY || Math.abs(parseInt(document.body.style.top || '0', 10));
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      }
    };
  });

  handleBackdropClick = () => {
    if (this.closeOnOutsideClick) {
      this.args.onClose();
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (this.closeOnEscapeKey && event.key === 'Escape') {
      event.stopPropagation();
      this.args.onClose();
    }
  };

  handleCloseButton = () => {
    this.args.onClose();
  };

  <template>
    {{! Anchor element for the lifecycle modifier — always in DOM }}
    <div
      class="reimagined-drawer-anchor sr-only"
      {{this.manageFocus @isOpen}}
      aria-hidden="true"
    ></div>

    {{#if this._mounted}}
      {{#in-element this.portalDestination insertBefore=null}}
        <div
          data-test-drawer
          data-drawer-id={{this.drawerId}}
          class="fixed inset-0 z-40 flex {{if @nonModal 'pointer-events-none'}}"
          role="dialog"
          aria-modal={{unless @nonModal "true"}}
          aria-label={{@ariaLabel}}
          tabindex="-1"
          {{on "keydown" this.handleKeyDown}}
        >
          {{#if @nonModal}}
            {{! No backdrop: the page behind a non-modal drawer stays usable. }}
          {{else if this.closeOnOutsideClick}}
            {{! template-lint-disable no-nested-interactive }}
            {{! template-lint-disable no-bare-strings }}
            <button
              data-test-drawer-backdrop
              type="button"
              class="absolute inset-0
                {{this.backdropClass}}
                transition-opacity duration-300 cursor-default
                {{if this._visible 'opacity-100' 'opacity-0'}}"
              aria-label="Close"
              tabindex="-1"
              {{on "click" this.handleBackdropClick}}
            ></button>
          {{else}}
            <div
              data-test-drawer-backdrop
              class="absolute inset-0
                {{this.backdropClass}}
                transition-opacity duration-300 cursor-default
                {{if this._visible 'opacity-100' 'opacity-0'}}"
              aria-hidden="true"
            ></div>
          {{/if}}

          {{! Drawer panel — ...attributes lands here (not the overlay) so a
              class from the call site shapes the panel authors are styling. }}
          <div
            data-test-drawer-panel
            class="absolute
              {{this.panelPositionClass}}
              {{this.panelSizeClass}}
              {{this.panelFillClass}}
              tio-col-flush tio-bg-surface tio-fg-primary transform transition-transform duration-300 ease-in-out
              {{this.panelShadowClass}}
              {{this.slideClass}}"
            ...attributes
          >
            {{#if this.showCloseButton}}
              <button
                data-test-drawer-close
                type="button"
                class="tio-drawer__close absolute top-3 right-3 z-10 p-1.5 rounded-lg tio-fg-disabled hover:tio-fg-secondary hover:tio-bg-sunken transition-colors"
                {{! template-lint-disable no-bare-strings }}
                aria-label={{if @closeLabel @closeLabel "Close drawer"}}
                {{on "click" this.handleCloseButton}}
              >
                <PhX @size={{20}} />
              </button>
            {{/if}}

            {{#if (has-block "header")}}
              <DrawerHeader>{{yield to="header"}}</DrawerHeader>
            {{/if}}

            {{#if (has-block "body")}}
              <DrawerBody>{{yield to="body"}}</DrawerBody>
            {{/if}}

            {{#if (has-block "footer")}}
              <DrawerFooter>{{yield to="footer"}}</DrawerFooter>
            {{/if}}

            {{! Default block — child manages own layout with DrawerHeader/Body/Footer }}
            {{#if (has-block)}}
              {{yield}}
            {{/if}}
          </div>
        </div>
      {{/in-element}}
    {{/if}}
  </template>
}

export { DrawerHeader, DrawerBody, DrawerFooter };
export type { DrawerSignature, Placement, Size };
